import { useEffect, useMemo } from "react";
import { useQuery } from "@apollo/client";
import Table, { TableHeading, TableControls, TableFooter } from "@/components/Table";
import SearchInput from "@/components/SearchInput";
import EmptyState from "@/components/EmptyState";
import usePagination from "@/hooks/usePagination";
import { useSearchWithParams } from "@/hooks/useSearchParams";
import usePageTitle from "@/hooks/usePageTitle";
import { useIsCompactLayout } from "@/hooks/useIsCompactLayout";
import type { Path } from "@/router";
import CommissionTabLayout from "../_CommissionTabLayout";
import { useCanShowCommissionPlatformBool, useRequireCommissionPlatform } from "../useCommissionPlatformGate";
import { COMMISSION_ENROLLMENTS_QUERY } from "./queries";
import { enrollmentPersonPgaId, getColumns } from "./_components/TableColumns";

export default function CommissionEnrollmentsPage() {
  usePageTitle("/billing/finance/commission/enrollments", "Enrollments");
  useRequireCommissionPlatform();
  const canQuery = useCanShowCommissionPlatformBool();
  const { isCompactLayout } = useIsCompactLayout();
  const { paginationVariables, createHeaderPagination, createFooterPagination, resetPagination } = usePagination();
  const { searchValue, debouncedSearch, setSearchValue, clearSearch } = useSearchWithParams({ paramName: "search" });
  const columns = useMemo(() => getColumns({ isCompactLayout }), [isCompactLayout]);

  useEffect(() => {
    resetPagination();
  }, [debouncedSearch, resetPagination]);

  const { data, previousData, loading, error } = useQuery(COMMISSION_ENROLLMENTS_QUERY, {
    variables: {
      ...paginationVariables,
      ...(debouncedSearch ? { search: debouncedSearch } : {})
    },
    skip: !canQuery
  });

  const connection = data?.commissionEnrollments ?? previousData?.commissionEnrollments;
  const items = connection?.nodes ?? [];
  const totalRowCount = connection?.totalCount ?? items.length;
  const hasActiveFilters = !!debouncedSearch;

  if (error) {
    return (
      <CommissionTabLayout>
        <EmptyState title="Error" caption="An error occurred while loading enrollments." iconName="error" />
      </CommissionTabLayout>
    );
  }

  return (
    <CommissionTabLayout>
      <TableHeading body="Members enrolled in commission benefit programs with Corebridge account status." />
      <Table
        data={items}
        columns={columns}
        loading={loading}
        hasActiveFilters={hasActiveFilters}
        getRowLink={(row) => {
          const pgaId = enrollmentPersonPgaId(row.original);
          return pgaId ? { to: `/people/${pgaId}` as Path } : null;
        }}
        renderNoDataEmptyState={() => (
          <EmptyState title="No Enrollments Yet." caption="Enrollments will appear here once synced." iconName="list" />
        )}
        renderFilteredEmptyState={() => (
          <EmptyState title="No Results." caption="Try a different search." iconName="search" />
        )}
        renderControls={(_table, { onSortClick } = {}) => (
          <TableControls
            pagination={createHeaderPagination(connection, {
              loading,
              pageCount: items.length,
              totalCount: totalRowCount
            })}
            onSortClick={onSortClick}
            activeFilters={hasActiveFilters ? 1 : 0}
            onClearAllFilters={() => {
              clearSearch();
              resetPagination();
            }}
          >
            <SearchInput
              query={searchValue}
              onChange={(value) => {
                setSearchValue(value);
                resetPagination();
              }}
              onClear={() => {
                clearSearch();
                resetPagination();
              }}
              placeholder="Search name or PGA ID"
            />
          </TableControls>
        )}
        renderFooter={() => (
          <TableFooter
            pagination={createFooterPagination(connection, {
              loading,
              pageCount: items.length,
              totalCount: totalRowCount
            })}
            totalRowCount={totalRowCount}
          />
        )}
      />
    </CommissionTabLayout>
  );
}
